<script lang="ts">
    import { getContext } from 'svelte'
    import { derived as sderived } from 'svelte/store'

    import Icon from '@iconify/svelte'

    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _ } from 'hydria/services/gettext'
    import { sanchor, spath } from 'hydria/svelte_helpers'

    import type { IDomain } from 'wdom/domain/interfaces'
    import { Breadcrumb } from 'breadcrumb'

    import type { DomainHandler } from './domain'

    import ArcServices from './ArcServicesBlock.svelte'
    import ContactHistoryBlock from './ContactHistoryBlock.svelte'
    import ContactsBlock from './ContactsBlock.svelte'
    import CustomNameserversBlock from './CustomNameserversBlock.svelte'
    import Delegates from './DelegatesBlock.svelte'
    import DsBlock from './DsBlock.svelte'
    import DsRecordsEdit from './DsRecordsEdit.svelte'
    import LastNotifications from './LastNotificationsBlock.svelte'
    import LogBlock from './LogBlock.svelte'
    import NameserverBlock from './NameserverBlock.svelte'
    import PropertyGroups from './PropertyGroupsBlock.svelte'
    import ZoneBlock from './ZoneBlock.svelte'

    const Domain = getContext('Domain') as DomainHandler

    const status = sderived(Domain.status, ($status) => $status)

    let domain: IDomain = $derived($status == 'ready' ? Domain.domain : ({} as IDomain))

    const crumbs = sderived(status, ($status) => [
        { txt: $_('Domaines'), url: spath('/domains') },
        {
            txt: $status == 'ready' ? Domain.unicode : $status == 'loading' ? '…' : iconExclamationTriangle,
            html: $status != 'ready' && $status != 'loading',
        },
    ])

    let showEditDsRecords: boolean | 'pre-import' = $state(false)

    function editDsRecords(autoImportFromZone: boolean = false) {
        showEditDsRecords = autoImportFromZone ? 'pre-import' : true
    }
    function closeDsRecords() {
        showEditDsRecords = false
    }

    const nsTaskRunning = sderived(Domain.runningTasks.nameserver, (v) => v)
    const dsTaskRunning = sderived(Domain.runningTasks.dsRecords, (v) => v)

    let dnssecOk = $derived(($dsTaskRunning || !$dsTaskRunning) && $status == 'ready' && (domain.ds_records == null || !domain.ds_records.length || !Domain.zoneDomainRelation.zone_published || Domain.zoneDomainRelation.ds_consistents))
    let nsOk = $derived(($nsTaskRunning || !$nsTaskRunning) && $status == 'ready' && (!Domain.zoneDomainRelation.zone_exists || !Domain.zoneDomainRelation.zone_published || Domain.zoneDomainRelation.ns_consistents))
    $inspect('nsOk:', nsOk)
</script>

<style>
    .contact-dns-groups {
        display: grid;
        grid-gap: 30px;

        grid-template-columns: 1fr;
        grid-template-rows: auto auto auto;
        grid-template-areas: 'ns' 'customns' 'contacts';
    }
    @media (min-width: 699px) and (max-width: 899px) {
        .contact-dns-groups {
            grid-template-columns: 1fr 1fr;
            grid-template-rows: 1fr 1fr;
            grid-template-areas: 'contacts ns' 'contacts customns';
        }
    }
    @media (min-width: 900px) {
        .contact-dns-groups {
            grid-template-columns: minmax(535px, 2fr) minmax(auto, 1fr);
            grid-template-rows: auto 1fr;
            grid-template-areas: 'contacts ns' 'contacts customns';
        }
    }

    .contact-dns-groups :global(.card-contacts) {
        grid-area: contacts;
    }
    .contact-dns-groups :global(.card-nameservers) {
        grid-area: ns;
    }
    .contact-dns-groups :global(.card-custom-nameservers) {
        grid-area: customns;
    }
    .contact-dns-groups > :global(.card) {
        margin-bottom: 0 !important;
    }

    #container-domain {
        gap: 30px;
    }
</style>

<Breadcrumb {crumbs} />

{#if $status == 'not-found' || $status == 'error'}
    <div class="alert alert-danger">
        <Icon inline icon="fa:exclamation-triangle" />
        {#if $status == 'not-found'}
            {$_('The requested document does not exist.')}
        {:else}
            {$_('An error occured. Please try again in a moment.')}
        {/if}
    </div>
{:else if $status == 'loading'}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div id="container-domain" class="d-grid">
        {#if !dnssecOk || !nsOk}
            <div class="card" class:card-warning={dnssecOk} class:card-danger={!dnssecOk}>
                <div class="card-title" class:text-danger={!dnssecOk} class:text-warning={!nsOk && dnssecOk} class:text-secondary={nsOk && dnssecOk}>Problèmes</div>
                {#if !nsOk}
                    <div class="card-body">
                        <p class="text-warning">Les serveurs de nom diffèrent de l’information de la zone.</p>
                        <p class="text-warning"><a use:sanchor href={spath(`/domain/${domain.idna}/nameservers?import-from-zone=1`)}>Importer de la zone</a></p>
                    </div>
                {/if}
                {#if !dnssecOk}
                    <div class="card-body">
                        <p class="text-danger">Les entrées DS sur ce domaine diffèrent de la signature de la zone. Cette zone peut être vue comme invalide, et ignorée par les clients.</p>
                        <p class="text-danger">
                            <button class="btn btn-link btn-a btn-a" type="button" onclick={() => editDsRecords(true)}>Importer de la zone</button>
                        </p>
                    </div>
                {/if}
            </div>
        {/if}

        <PropertyGroups />

        <div class="domain-blocks-container">
            <div class="contact-dns-groups">
                <ContactsBlock />
                <NameserverBlock />
                <CustomNameserversBlock />
            </div>
        </div>

        <div class="row">
            <div class="col-md-6 d-grid"><ZoneBlock /></div>
            <div class="col-md-6 d-grid gap-4">
                <Delegates />
                <ArcServices />
            </div>
        </div>

        {#if domain.dnssec_support}
            <DsBlock editDsRecords={() => editDsRecords()} />
        {/if}

        <LastNotifications />

        <LogBlock />

        <ContactHistoryBlock />
    </div>

    {#if showEditDsRecords != false}
        <DsRecordsEdit close={() => closeDsRecords()} preImport={showEditDsRecords == 'pre-import'} />
    {/if}
{/if}
